import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Search } from "lucide-react";
import { SCField, SCModal, SCSection, SCEmpty, useConfirm } from "./Common";
import {
    scAdminListSifs, scAdminCreateSif, scAdminPatchSif, scAdminDeleteSif,
    scAdminListAmcs,
} from "../../../lib/sifcontrolApi";

const STRATEGY = ["Equity Long-Short", "Equity Ex-Top 100", "Hybrid", "Debt", "Multi Asset", "Other"];

const empty = () => ({
    sif_code: "", sif_name: "", short_name: "", amc_id: "", isin: "",
    strategy_category: "Other", strategy_name: "", investment_style: "",
    benchmark: "", launch_date: "", minimum_investment: 0, exit_load: "",
    plan: "Regular", option: "Growth", status: "active",
    fund_manager: "", co_fund_manager: "", investment_objective: "",
    strategy_description: "", factsheet_url: "",
});

export default function SifsTab() {
    const [items, setItems] = useState([]);
    const [amcs, setAmcs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [amcFilter, setAmcFilter] = useState("");
    const [modal, setModal] = useState(null);
    const [saving, setSaving] = useState(false);
    const { confirm, dialog } = useConfirm();

    const load = async () => {
        setLoading(true);
        try {
            const [s, a] = await Promise.all([scAdminListSifs({ q, amc_id: amcFilter }), scAdminListAmcs()]);
            setItems(s.items || []);
            setAmcs(a.items || []);
        } catch {
            toast.error("Could not load SIFs");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, amcFilter]);

    const amcOptions = useMemo(() => [
        { value: "", label: "— Select AMC —" },
        ...amcs.map((a) => ({ value: a.id, label: a.amc_name })),
    ], [amcs]);
    const amcNameById = useMemo(() => Object.fromEntries(amcs.map((a) => [a.id, a.amc_name])), [amcs]);

    const openCreate = () => {
        if (amcs.length === 0) {
            toast.error("Add an AMC first.");
            return;
        }
        setModal({ mode: "create", data: { ...empty(), amc_id: amcs[0].id } });
    };
    const openEdit = (row) => setModal({ mode: "edit", data: { ...row } });

    const save = async () => {
        const d = modal.data;
        if (!d.sif_code?.trim()) return toast.error("SIF code is required");
        if (!d.sif_name?.trim() || d.sif_name.trim().length < 2) return toast.error("SIF name is required");
        if (!d.amc_id) return toast.error("AMC is required");
        setSaving(true);
        try {
            if (modal.mode === "create") {
                await scAdminCreateSif(d);
                toast.success("SIF added");
            } else {
                const { id, created_at, updated_at, ...rest } = d;  // eslint-disable-line no-unused-vars
                await scAdminPatchSif(id, rest);
                toast.success("SIF updated");
            }
            setModal(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const del = async (row) => {
        if (!(await confirm(`Delete "${row.sif_name}"? This can't be undone.`))) return;
        try {
            await scAdminDeleteSif(row.id);
            toast.success("SIF deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const setF = (k, v) => setModal({ ...modal, data: { ...modal.data, [k]: v } });

    return (
        <div data-testid="sc-admin-sifs">
            <SCSection
                title="SIFs"
                subtitle="Specialized Investment Funds. Match keys: SIF Code + ISIN."
                right={
                    <button onClick={openCreate} className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3" data-testid="sc-sif-add-btn">
                        <Plus size={14} /> Add SIF
                    </button>
                }
                testId="sc-sifs-header"
            />
            <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="relative flex-1 min-w-[240px] max-w-[400px]">
                    <Search size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-oth-ink3" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="w-full h-10 pl-9 pr-3 rounded-full border border-oth-line/70 bg-white text-sm focus:outline-none focus:border-oth-blue"
                        placeholder="Search by name, code or ISIN"
                        data-testid="sc-sifs-search"
                    />
                </div>
                <select
                    value={amcFilter}
                    onChange={(e) => setAmcFilter(e.target.value)}
                    className="h-10 px-4 rounded-full border border-oth-line/70 bg-white text-sm focus:outline-none focus:border-oth-blue"
                    data-testid="sc-sifs-amcfilter"
                >
                    <option value="">All AMCs</option>
                    {amcs.map((a) => <option key={a.id} value={a.id}>{a.amc_name}</option>)}
                </select>
            </div>

            {loading ? (
                <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>
            ) : items.length === 0 ? (
                <SCEmpty message="No SIFs match." testId="sc-sifs-empty" />
            ) : (
                <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden">
                    <div className="grid grid-cols-[1.6fr_140px_150px_120px_90px_100px] text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body px-5 py-3 bg-oth-bg-2/60 border-b border-oth-line/70">
                        <div>SIF</div>
                        <div>Code / ISIN</div>
                        <div>AMC</div>
                        <div>Strategy</div>
                        <div>Status</div>
                        <div className="text-right">Actions</div>
                    </div>
                    <div className="divide-y divide-oth-line/60" data-testid="sc-sifs-list">
                        {items.map((s) => (
                            <div key={s.id} className="grid grid-cols-[1.6fr_140px_150px_120px_90px_100px] items-center px-5 py-3.5" data-testid={`sc-sif-row-${s.id}`}>
                                <div>
                                    <div className="font-display text-sm font-medium text-oth-ink">{s.sif_name}</div>
                                    <div className="text-[11px] font-body text-oth-ink3 mt-0.5">{s.short_name || " "}</div>
                                </div>
                                <div className="text-xs font-body text-oth-ink2">
                                    <div>{s.sif_code || "—"}</div>
                                    <div className="text-[11px] text-oth-ink3">{s.isin || "no ISIN"}</div>
                                </div>
                                <div className="text-xs font-body text-oth-ink2 truncate">{amcNameById[s.amc_id] || "—"}</div>
                                <div className="text-xs font-body text-oth-ink2 truncate">{s.strategy_category}</div>
                                <div className="text-xs font-body">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${s.status === "active" ? "bg-green-100 text-green-800" : "bg-oth-bg-2 text-oth-ink3"}`}>{s.status}</span>
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <button onClick={() => openEdit(s)} className="w-8 h-8 rounded-full hover:bg-oth-bg-2 flex items-center justify-center" data-testid={`sc-sif-edit-${s.id}`} aria-label="Edit"><Edit3 size={13} /></button>
                                    <button onClick={() => del(s)} className="w-8 h-8 rounded-full hover:bg-red-50 text-oth-ink3 hover:text-red-700 flex items-center justify-center" data-testid={`sc-sif-delete-${s.id}`} aria-label="Delete"><Trash2 size={13} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <SCModal
                open={!!modal}
                title={modal?.mode === "create" ? "Add SIF" : "Edit SIF"}
                onClose={() => !saving && setModal(null)}
                testId="sc-sif-modal"
                maxWidth={720}
                footer={
                    <>
                        <button onClick={() => setModal(null)} disabled={saving} className="btn-pill !text-sm" data-testid="sc-sif-modal-cancel">Cancel</button>
                        <button onClick={save} disabled={saving} className="btn-pill btn-pill-dark !text-sm" data-testid="sc-sif-modal-save">{saving ? "Saving…" : "Save"}</button>
                    </>
                }
            >
                {modal && (
                    <div>
                        <div className="grid grid-cols-2 gap-3">
                            <SCField label="SIF Code" value={modal.data.sif_code} onChange={(v) => setF("sif_code", v)} testId="sc-sif-form-code" required hint="Internal / AMFI code" />
                            <SCField label="ISIN" value={modal.data.isin} onChange={(v) => setF("isin", v)} testId="sc-sif-form-isin" hint="Preferred match key" />
                        </div>
                        <SCField label="SIF name" value={modal.data.sif_name} onChange={(v) => setF("sif_name", v)} testId="sc-sif-form-name" required />
                        <SCField label="Short name" value={modal.data.short_name} onChange={(v) => setF("short_name", v)} testId="sc-sif-form-short" />
                        <div className="grid grid-cols-2 gap-3">
                            <SCField label="AMC" value={modal.data.amc_id} onChange={(v) => setF("amc_id", v)} testId="sc-sif-form-amc" type="select" options={amcOptions} required />
                            <SCField label="Strategy category" value={modal.data.strategy_category} onChange={(v) => setF("strategy_category", v)} testId="sc-sif-form-strategy-cat" type="select" options={STRATEGY} />
                        </div>
                        <SCField label="Strategy name" value={modal.data.strategy_name} onChange={(v) => setF("strategy_name", v)} testId="sc-sif-form-strategy-name" placeholder="e.g. Long-short Equity, Ex-Top 100 Growth" />
                        <div className="grid grid-cols-2 gap-3">
                            <SCField label="Investment style" value={modal.data.investment_style} onChange={(v) => setF("investment_style", v)} testId="sc-sif-form-style" />
                            <SCField label="Benchmark" value={modal.data.benchmark} onChange={(v) => setF("benchmark", v)} testId="sc-sif-form-benchmark" placeholder="e.g. Nifty 500 TRI" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <SCField label="Launch date" value={modal.data.launch_date} onChange={(v) => setF("launch_date", v)} testId="sc-sif-form-launch" type="date" />
                            <SCField label="Minimum investment (₹)" value={modal.data.minimum_investment} onChange={(v) => setF("minimum_investment", v)} testId="sc-sif-form-min" type="number" />
                        </div>
                        <SCField label="Exit load" value={modal.data.exit_load} onChange={(v) => setF("exit_load", v)} testId="sc-sif-form-exit" />
                        <div className="grid grid-cols-2 gap-3">
                            <SCField label="Plan" value={modal.data.plan} onChange={(v) => setF("plan", v)} testId="sc-sif-form-plan" type="select" options={["Regular", "Direct"]} />
                            <SCField label="Option" value={modal.data.option} onChange={(v) => setF("option", v)} testId="sc-sif-form-option" type="select" options={["Growth", "Dividend", "IDCW"]} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <SCField label="Fund manager" value={modal.data.fund_manager} onChange={(v) => setF("fund_manager", v)} testId="sc-sif-form-fm" />
                            <SCField label="Co-fund manager" value={modal.data.co_fund_manager} onChange={(v) => setF("co_fund_manager", v)} testId="sc-sif-form-cofm" />
                        </div>
                        <SCField label="Factsheet URL" value={modal.data.factsheet_url} onChange={(v) => setF("factsheet_url", v)} testId="sc-sif-form-factsheet" placeholder="https://…" />
                        <SCField label="Investment objective" value={modal.data.investment_objective} onChange={(v) => setF("investment_objective", v)} testId="sc-sif-form-obj" type="textarea" />
                        <SCField label="Strategy description" value={modal.data.strategy_description} onChange={(v) => setF("strategy_description", v)} testId="sc-sif-form-desc" type="textarea" />
                        <SCField label="Status" value={modal.data.status} onChange={(v) => setF("status", v)} type="select" options={["active", "inactive"]} testId="sc-sif-form-status" />
                    </div>
                )}
            </SCModal>
            {dialog}
        </div>
    );
}
